// import { _token } from "./currentUser";
import { csrfToken } from "$lib/stores/csrfTokenStore";
import { get } from "svelte/store";
import { getClassicWebUrl, getCSRF } from "./currentUser";

const MAX_RETRY_ATTEMPTS_FOR_CLASSIC_WEB_CALLS = 3;

type SupportedTypes = number | string | undefined | boolean | { [key: string]: any };

type JsonResponse<TResult extends SupportedTypes = {}> = {
	errorCode?: string;
	code?: string;
	errorMessage?: string;
	extraData?: {};
} & {
	[P in keyof TResult]: TResult[P];
};

const SUPPORTED_METHODS = ["GET", "POST", "PUT", "DELETE"] as const;

const DEFAULT_ERROR_CODE_FROM_STATUS_CODE: Record<number, string> = {
	401: "unauthorized",
	404: "not_found"
};
const FALLBACK_ERROR_CODE = "unknown";

/**
 * Sends JSON calls to server
 * Use with Python's @json_post_and_track_errors/@json_get_and_track_errors decorated methods and SvelteKit API endpoints
 *
 * @param url
 * @param params
 */
async function sendJson<TResult extends SupportedTypes = {}>(
	url: string,
	params: {} | FormData | null = null,
	method: (typeof SUPPORTED_METHODS)[number],
	retryAttempt: number = 0
): Promise<TResult> {
	if (!SUPPORTED_METHODS.includes(method)) {
		throw new Error(`Unsupported method: ${method}`);
	}

	const csrf_token = getCSRF();
	if (csrf_token) {
		if (!params) {
			params = {};
		}
		(params as any)["_token"] = csrf_token;
	}

	const body = JSON.stringify(params);

	let response;
	try {
		response = await fetch(url, {
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
				"Referrer-Policy": "origin-when-cross-origin",
				"Access-Control-Allow-Headers": "Content-Type, Referrer-Policy, Authorization, CSRF-Token",
				"CSRF-Token": get(csrfToken)
			},
			body: method === "GET" ? undefined : body,
			mode: "cors",
			method,
			credentials: "include"
		});
	} catch (e) {
		if (url.startsWith(getClassicWebUrl()) && retryAttempt < MAX_RETRY_ATTEMPTS_FOR_CLASSIC_WEB_CALLS) {
			const sleepDurationInSeconds = Math.pow(2, retryAttempt) * 0.1;
			await sleep(sleepDurationInSeconds);
			return sendJson(url, params, method, retryAttempt + 1);
		}

		throw new Error(`HTTP ${method} JSON Error ${url}, ${(e as Error).message}`);
	}

	let json: JsonResponse<TResult>;
	try {
		json = await response.json();
	} catch (e) {
		throw new ServerError();
	}

	if (response.status > 299) {
		// Extract error code from response. "code" is from new-app responses, "errorCode" is from the old Python responses.
		let errorCode = json.code || json.errorCode;
		if (!errorCode) {
			errorCode = DEFAULT_ERROR_CODE_FROM_STATUS_CODE[response.status] || FALLBACK_ERROR_CODE;
		}

		// Message is extracted from old Python responses. New-app responses don't have a message.

		throw new ServerError(errorCode, json.errorMessage, json.extraData);
	}

	return json as TResult;
}

/**
 * Sends a GET request to server - use with @json_get_and_track_errors  decorated methods and SvelteKit API endpoints
 *
 * @param url
 */
export async function getJson<TResult extends SupportedTypes>(url: string): Promise<TResult> {
	return sendJson<TResult>(url, null, "GET");
}

/**
 * Sends a POST request to server - use with @json_post_and_track_errors  decorated methods and SvelteKit API endpoints
 *
 * @param url
 * @param params
 */
export async function postJson<TResult extends SupportedTypes>(url: string, params: {} | FormData | null = null): Promise<TResult> {
	return sendJson<TResult>(url, params, "POST");
}

/**
 * Sends a DELETE request to server
 *
 * @param url
 * @param params
 */
export async function deleteJson<TResult extends SupportedTypes>(url: string, params: {} | FormData | null = null): Promise<TResult> {
	return sendJson<TResult>(url, params, "DELETE");
}

export async function postForm(url: string, params?: {} | FormData): Promise<string> {
	const body = createBody(params);

	let response;
	try {
		response = await fetch(url, {
			body,
			method: "POST",
			credentials: "include"
		});
	} catch (e) {
		throw new Error(`HTTP POST FORM Error ${url}, ${(e as Error).message}`);
	}

	if (response.status === 401) {
		// Force redirect to homepage
		document.location.href = "/";
		throw new Error("redirect required");
	}

	const t = await response.text();
	return t;
}

function createBody(input: { [id: string]: string | number } | FormData | undefined): FormData | null {
	if (!input) {
		return null;
	}

	if (input instanceof FormData) {
		return input;
	}

	const data = new FormData();

	Object.keys(input).forEach((k) => {
		const value = input[k];

		if (Array.isArray(value)) {
			// Handle the special, but annoying case of sending an array in formData
			const arrayKey = k.indexOf("[]") > 0 ? k : `${k}[]`;
			value.forEach((v) => {
				data.append(arrayKey, String(v));
			});
		} else {
			data.set(k, String(input[k]));
		}
	});

	return data;
}

// To find user errors -
export class ServerError extends Error {
	static readonly GENERIC_ERROR = "Oops, something went wrong";

	constructor(
		public code?: string,
		message?: string,
		public extraData?: Record<any, any>
	) {
		// In case we got an non-UserException error from the server (see "ApiException" class in server), hide the real exception message.
		super(!code || code === FALLBACK_ERROR_CODE ? ServerError.GENERIC_ERROR : message);
	}

	toMessage(messagesByCode: { [code: string]: string }) {
		return (messagesByCode && messagesByCode[this.code]) || ServerError.GENERIC_ERROR;
	}
}

async function sleep(seconds: number) {
	return new Promise((resolve) => {
		setTimeout(resolve, seconds * 1000);
	});
}
